import {
    getDownloadLink,
} from "../../api/filesView_api";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {DataTable} from "../ui/dataTable";
import {FileSecurityProps, FolderView} from "@/src/components/files/files";
import {FileView} from "@/src/components/files/files";
import {
    WithLoggedInAuthInfoProps,
    withRequiredAuthInfo,
} from "@propelauth/react";
import {ColumnDef} from "@tanstack/react-table";
import React, {useState, useEffect, useContext} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    DownloadIcon,
    TrashIcon,
    ArchiveIcon,
    FileIcon,
    Link1Icon,
    Link2Icon,
    QuestionMarkCircledIcon,
} from "@radix-ui/react-icons";
import {Button} from "../ui/button";
import {SortableHeader} from "../ui/tableUtils";
import {DragAndDropUploadv2} from "./DragAndUploadFile";
import {ProjectContext} from "../../contexts/ProjectContext";
import {CompanyType} from "../../models/company";
import {API_URL, fetchWithToken, deleteWithToken} from "../../api/apiBase";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
} from "../ui/breadcrumb";
import {makeZipfromUrls} from "./downloadAll";
import {AddLinkDialog} from "./LinkAdd";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip"
import { Spinner } from "../ui/spinner";
import {useToast} from "../ui/use-toast";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";
import { ArrowDownToLine, Folder } from 'lucide-react';

type FileUploadProps = WithLoggedInAuthInfoProps & {
    uploadContext: FileSecurityProps;
};

function showDeleteFiles(user_type: string | undefined, url: string) {
    /**
     * there are 3 things that can have files: projects, bid, VEItem. (for now)
     *
     * Permissions are all validated on the backend, so this is for ux purposes only.
     * */
    if (!user_type) {
        return false;
    } else if (url.includes("/bid/")) {
        return user_type === CompanyType.builder;
    } else if (url.includes("/ve/")) {
        return user_type === CompanyType.builder;
    } else {
        return user_type === CompanyType.developer;
    }
}


/**
 * This component is used as a page view, as well as a widget view.
 * We probably need to make a widget version...
 *
 * TODO - clicking on folders is kinda slow.
 * TODO - maybe a method to download many files at once??
 * */
const FilesView: React.FC<FileUploadProps> = ({uploadContext, ...props}) => {
    const [files, setFiles] = useState<FileView[]>([]);
    const path = useLocation().pathname;
    const [folderId, setFolderId] = useState<string | null>(null)
    const location = uploadContext.url ? uploadContext.url : path;
    const userProjectContext = useContext(ProjectContext);
    // This is just so the state updates and effect runs - if you know a better way, you can do.
    const [ready, setReady] = useState<boolean>(false);
    const [selected, setSelected] = useState<FileView | null>(null);
    const [folderHist, setFolderHist] = useState<FileView[]>([])
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const navigate = useNavigate();
    const {project_id} = useParams();
    const {toast} = useToast();
    // this is the folder that is open in the current view
    // it is passed in , but we keep an override
    const ActiveFolderID = folderId ? folderId : uploadContext.folder_id

    useEffect(() => {
        fetchWithToken(
            `/files/list/v2/${ActiveFolderID}`, props.accessToken)
            .then((data) => {
            // console.log(data);
            setFiles(data);
         });
    }, [
        ActiveFolderID,
        uploadContext.project_id,
        uploadContext.parent_id,
        uploadContext.parent_type,
        ready,
    ]);

    const onOpenFolder = (folder_id: string) => {
        // when we open a folder - we
        fetchWithToken(`/files/list/v2/${folder_id}`, props.accessToken)
            .then((data) => {
                setFiles(data);
                setFolderId(folder_id)
            });
    }

    const onDeleteFile = (file: FileView) => {
        if (uploadContext.project_id && uploadContext.parent_type) {
            deleteWithToken(
                `/files/delete/v1/${project_id}/${uploadContext.parent_type}/${file.id}`,
                props.accessToken
            ).then((x) => {
                // This is just so the state updates and effect runs
                setReady(!ready);
            });
        }
    };

    const onFileAdded = (new_file: FileView | any) => {
        setFiles((currentFiles) => [...currentFiles, new_file]);
    };

    const handleFileClick = (event: any, file_or_folder: FileView) => {
        if (file_or_folder.is_folder) {
            setFolderHist((prev) => [...prev, file_or_folder])
            onOpenFolder(file_or_folder.id)
        } else if (file_or_folder.is_link) {
            // @ts-ignore
            window.open(file_or_folder.value, "_blank")
        } else {
            // maybe do nothing? maybe download ? lets see how it feels.
        }
    }

    const handleNavClick = (folder: FileView) => {
        const ds = []
        for (let i = 0; i < folderHist.length; i++) {
            ds.push(folderHist[i])
            if (folderHist[i].id === folder.id) {
                break;
            }
        }
        setFolderHist(ds);
        onOpenFolder(folder.id)
    }

    const handleHomeClick = () => {
        setFolderHist([])
        if (uploadContext.folder_id) {
            onOpenFolder(uploadContext.folder_id)
        }
    }

    const createFolder = async (e: any) => {
        // todo - this is the api call for creating a folder ...
        // i want to keep the editing stuff in a seperate component -
        // just not sure how to organize it
        // probably we just do it here
        fetchWithToken(`/files/add_folder/v1/${ActiveFolderID}`, props.accessToken)
            .then((data) => {
                onFileAdded(data);
            })
            .then((err) => {
                toast({
                    title: "Error",
                    description: "Could not create folder: " + String(err),
                    variant: "destructive"
                });
            })
    };

    const DownloadAll = async (folder_id: string) => {
        setIsDownloading(true);
        const response = await fetch(
            `${API_URL}/files/download_all_links/v1/${folder_id}`,
            {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${props.accessToken}`,
                },
            })
        const data = await response.json();
        console.log(data)
        await makeZipfromUrls(data)
        setIsDownloading(false);
    }

    const handleDownload = (file: FileView | FolderView) => {
        if (file.is_folder) {
            DownloadAll(file.id)
        } else {
            getDownloadLink(file, uploadContext, props)
        }
    }

    const onRename = (e: any, file: FileView) => {
        // todo -
    };

    const _columns: ColumnDef<FileView>[] = [
        {
            header: ({column}) => (<SortableHeader column={column} name={"Name"}/>),
            accessorKey: "name",
            cell: ({row}) => {
                if (uploadContext.editing) {
                    if (selected && row.original.id === selected.id) {
                        // todo
                        return (
                            <input
                                value={selected.name!}
                                onChange={(e) => onRename(e, row.original)}
                            />
                        );
                    }
                }
                let fileView = row.original;
                if (fileView.is_folder) {
                    return <span className="flex items-center gap-3">
                            <ArchiveIcon/>
                            <a onClick={(e) => handleFileClick(e, fileView)}>
                                {fileView.name}
                            </a>
                        </span>
                } else if (fileView.is_link) {
                    return <span>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <span className="flex items-center gap-3">
                                        <Link2Icon/>
                                        <a onClick={(e) => handleFileClick(e, fileView)}>
                                            {fileView.name}
                                        </a>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{fileView.value}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </span>
                } else {
                    return <span className="flex items-center gap-3">
                        <FileIcon/>
                        <a onClick={(e) => handleFileClick(e, fileView)}>
                            {fileView.name}
                        </a>
                    </span>
                }
            },
        },
        {
            header: ({column}) => (<SortableHeader column={column} name={"Created At"}/>),
            accessorKey: "created_at",
            cell: ({row}) => {
                const date = new Date(row.original.created_at);
                return <span>{date.toLocaleDateString()}</span>;
            },
        },
        {
            header: "Author",
            accessorKey: "created_by.name",
        },
        {
            header: "Actions",
            accessorKey: "path_key",
            cell: ({row}) => {
                // todo - these shoulf be spaced farther apart, and have some sort of
                //  popeover telling user what hte icons mean .
                return (
                    <div className="flex">
                        <div>
                            {!row.original.is_link ?
                                <button
                                    className="mr-1"
                                    onClick={() => handleDownload(row.original)}
                                >
                                    <DownloadIcon width={17} height={17}/>
                                </button>
                                : <></>
                            }
                            <button
                                className="mr-5"
                                onClick={(e) => { e.stopPropagation(); }}
                            >
                            </button>
                        </div>

                        {showDeleteFiles(userProjectContext?.role, location) ? (
                            <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <span className="">
                                                <span onClick={() => onDeleteFile(row.original)}>
                                                    <TrashIcon width={17} height={17}/>
                                                </span>
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Delete</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                        ) : (
                            <></>
                        )}
                    </div>
                );
            },
        },
    ];

    if (!files) {
        return <div><BaseLoadingSpinner/></div>;
    }
    return (
        <Card>
            <CardHeader>
                <CardTitle className="columns-1 flex items-center justify-between">

                    <div className=" ">Files</div>
                    <div className=" justify-end">
                        <Button variant="outline" onClick={
                            () => {
                                navigate(`/project/${project_id}/documents`)
                            }}>More</Button>
                    </div>

                </CardTitle>
            </CardHeader>
            {/*todo - move editing functionality here-ish */}
            <CardContent>
                <div className="flex columns-2 justify-between">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbSeparator></BreadcrumbSeparator>
                            <BreadcrumbItem>
                                <button onClick={() => handleHomeClick()}>Home</button>
                            </BreadcrumbItem>
                            {folderHist.map((x, i) => (
                                <>
                                    <BreadcrumbSeparator></BreadcrumbSeparator>
                                    <BreadcrumbItem>
                                        <button onClick={() => handleNavClick(x)}>{x.name}</button>
                                    </BreadcrumbItem>
                                </>
                            ))}
                        </BreadcrumbList>
                    </Breadcrumb>
                    <div className="flex gap-2 mb-2">

                        {/* Downloads */}
                        <div className="flex items-center gap-2">
                        {isDownloading ? <span><Spinner size="small"/></span> : <></>}
                        <Button
                            variant="outline"
                            disabled={isDownloading}
                            onClick={(e) => {
                                DownloadAll(ActiveFolderID as string)
                            }}>Download All <ArrowDownToLine size={16} className="ml-2"/>
                        </Button>
                        </div>


                        {/* Link and folder editing  */}
                         {uploadContext.editing && ActiveFolderID
                             ? (<>
                                 <Button
                                    variant="outline"
                                    className="text-sm py-2 px-4 rounded-lg transition duration-300"
                                    onClick={createFolder}
                                    >
                                    Add Folder <Folder size={16} className="ml-2"/>
                                </Button>
                                 <AddLinkDialog
                                    folder_id={ActiveFolderID}
                                    onAddFile={onFileAdded}
                                 />
                                 </>
                             )
                             : <></>}
                    </div>
                </div>

                <DataTable data={files} columns={_columns}/>

                {uploadContext.editing && ActiveFolderID ? (
                    <div className=" justify-end">
                        <DragAndDropUploadv2
                            folder_id={ActiveFolderID}
                            onAddFile={onFileAdded}
                        />
                    </div>
                ) : (
                    <></>
                )}
            </CardContent>
        </Card>
    );
};

export default withRequiredAuthInfo(FilesView);
